/**
 * Coalesce xpath / selector strategies from testStep WS payloads and executionFlow actions.
 */

function normalizeStrategyEntry(strategy, index = 0) {
    if (!strategy) return null;
    if (typeof strategy === 'string') {
        const value = strategy.trim();
        if (!value) return null;
        return {
            type: 'xpath_custom',
            selector: value,
            xpath: value,
            enabled: true,
            priority: index,
        };
    }
    const selector = strategy.selector ?? strategy.xpath ?? strategy.value;
    if (!selector) return null;
    return {
        ...strategy,
        type: strategy.type ?? 'xpath_playwright',
        selector,
        xpath: strategy.xpath ?? selector,
        enabled: strategy.enabled !== false,
        priority: strategy.priority ?? index,
    };
}

function normalizeStrategyList(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((entry, index) => normalizeStrategyEntry(entry, index)).filter(Boolean);
}

function strategyFromExecuted(executed) {
    const normalized = normalizeStrategyEntry(executed);
    return normalized ? [normalized] : [];
}

/** Build xpath strategies from executionFlow `details` (live recording stream). */
export function buildStrategiesFromActionDetails(details = null) {
    if (!details || typeof details !== 'object') return [];

    const entries = [
        { type: 'xpath_target', xpath: details.targetXPath },
        { type: 'xpath_resilient', xpath: details.resilientXPath },
        { type: 'xpath_positional', xpath: details.positionalXPath },
        { type: 'xpath_target', xpath: details.endTargetXPath },
        { type: 'xpath_resilient', xpath: details.endResilientXPath },
    ];

    const genericXPath = details.xpath;
    if (genericXPath && !entries.some((e) => e.xpath === genericXPath)) {
        entries.push({ type: 'xpath_custom', xpath: genericXPath });
    }

    const strategies = [];
    let priority = 0;
    for (const { type, xpath } of entries) {
        if (typeof xpath !== 'string' || !xpath.trim()) continue;
        strategies.push({
            type,
            selector: xpath.trim(),
            xpath: xpath.trim(),
            enabled: true,
            priority: priority++,
        });
    }

    return strategies;
}

/**
 * @returns {object[]|undefined} strategies array, or undefined if none found
 */
export function coalesceExecutionStrategies(testStep = null, action = null) {
    const candidates = [
        testStep?.executionStrategies,
        action?.executionStrategies,
        testStep?.details?.executionStrategies,
        action?.details?.executionStrategies,
        testStep?.strategies,
        action?.strategies,
    ];

    for (const raw of candidates) {
        const normalized = normalizeStrategyList(raw);
        if (normalized.length > 0) return normalized;
    }

    const selectorLists = [
        testStep?.selectors,
        action?.selectors,
        action?.details?.selectors,
    ];
    for (const raw of selectorLists) {
        const normalized = normalizeStrategyList(raw);
        if (normalized.length > 0) return normalized;
    }

    const fromDetails = [
        ...buildStrategiesFromActionDetails(testStep?.details),
        ...buildStrategiesFromActionDetails(action?.details),
    ];
    if (fromDetails.length > 0) return fromDetails;

    const executed =
        testStep?.executedStrategy ??
        testStep?.result?.executedStrategy ??
        testStep?.result?.result?.executedStrategy ??
        action?.result?.executedStrategy ??
        action?.result?.result?.executedStrategy ??
        action?.executedStrategy;

    const fromExecuted = strategyFromExecuted(executed);
    if (fromExecuted.length > 0) return fromExecuted;

    return undefined;
}

/** Register a testStep under every key the stream may use for lookup. */
export function collectTestStepIndexKeys(step) {
    if (!step || typeof step !== 'object') return [];
    const keys = new Set();
    const add = (value) => {
        if (value != null && value !== '') keys.add(String(value));
    };

    add(step.executionId);
    add(step.id);
    add(step.mainExecutionId);
    add(step.executionGroupId);
    add(step.details?.executionId);

    if (step.stepIndex != null && Number.isFinite(Number(step.stepIndex))) {
        add(`stepIndex:${Number(step.stepIndex)}`);
    }

    return [...keys];
}

export function findMatchingTestStep(testStepsByExecutionId, executionGroup, action, flatStepIndex) {
    if (!testStepsByExecutionId || typeof testStepsByExecutionId !== 'object') return null;

    const candidates = [
        executionGroup?.id,
        action?.id,
        action?.executionId,
        action?.details?.executionId,
    ].filter((v) => v != null && v !== '');

    for (const key of candidates) {
        const hit = testStepsByExecutionId[key] ?? testStepsByExecutionId[String(key)];
        if (hit) return hit;
    }

    if (flatStepIndex != null) {
        const byIndex = testStepsByExecutionId[`stepIndex:${flatStepIndex}`];
        if (byIndex) return byIndex;
    }

    return null;
}

export function mergeExecutionStrategies(...sources) {
    for (const source of sources) {
        const coalesced = Array.isArray(source)
            ? normalizeStrategyList(source)
            : coalesceExecutionStrategies(source);
        if (Array.isArray(coalesced) && coalesced.length > 0) return coalesced;
    }
    return undefined;
}
